/**
 * 微信登录 Hook
 * 管理微信 SDK 加载、设备检测和登录流程
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { isMobileDevice, isWeChatBrowser } from "@/lib/device-detection";

declare global {
  interface Window {
    WxLogin?: new (config: WxLoginConfig) => void;
  }
}

interface WxLoginConfig {
  id: string;
  appid: string;
  scope: string;
  redirect_uri: string;
  state: string;
  self_redirect: boolean;
  style?: string;
  href?: string;
}

export type WechatLoginErrorType =
  | "SDK_LOAD_FAILED"
  | "CONFIG_MISSING"
  | "NETWORK_ERROR"
  | "TIMEOUT";

export interface WechatLoginError {
  type: WechatLoginErrorType;
  message: string;
}

export type SdkStatus = "idle" | "loading" | "loaded" | "error";

export interface WechatLoginConfig {
  appId: string;
  redirectUri: string;
  scope: string;
  state: string;
}

export interface UseWechatLoginOptions {
  /** 自定义 appId，默认从 meta 标签读取 */
  appId?: string;
  /** 自定义 redirectUri，默认从 meta 标签读取 */
  redirectUri?: string;
  /** 错误回调 */
  onError?: (error: WechatLoginError) => void;
  /** 是否自动预加载 SDK */
  autoPreload?: boolean;
  /** 登录成功后重定向路径 */
  redirectTo?: string;
}

export interface UseWechatLoginReturn {
  /** SDK 加载状态 */
  sdkStatus: SdkStatus;
  /** 是否为移动设备 */
  isMobile: boolean;
  /** 是否在微信浏览器中 */
  isWeChat: boolean;
  /** 配置是否有效 */
  isConfigValid: boolean;
  /** 弹窗是否打开 */
  dialogOpen: boolean;
  /** 设置弹窗状态 */
  setDialogOpen: (open: boolean) => void;
  /** 处理登录点击 */
  handleLogin: () => void;
  /** 预加载 SDK */
  preloadSdk: () => Promise<void>;
  /** 刷新二维码（生成新 state） */
  refreshQRCode: () => void;
  /** 登录配置 */
  config: WechatLoginConfig;
  /** 最后一次错误 */
  error: WechatLoginError | null;
}

const WECHAT_SDK_URL =
  "https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js";
const SDK_LOAD_TIMEOUT = 10000;

/** 生成随机 state */
function generateState(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** 从 meta 标签读取配置 */
function getMetaContent(name: string): string {
  if (typeof document === "undefined") return "";
  const meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  return meta?.content || "";
}

export function useWechatLogin(
  options: UseWechatLoginOptions = {}
): UseWechatLoginReturn {
  const { onError, autoPreload = true, redirectTo = "/" } = options;

  // 状态
  const [sdkStatus, setSdkStatus] = useState<SdkStatus>("idle");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState<WechatLoginError | null>(null);
  const [state, setState] = useState(generateState);

  // 设备检测
  const [isMobile, setIsMobile] = useState(false);
  const [isWeChat, setIsWeChat] = useState(false);

  // 配置
  const appId = options.appId || getMetaContent("wechat-app-id");
  const redirectUri =
    options.redirectUri || getMetaContent("wechat-redirect-uri");
  const isConfigValid = Boolean(appId && redirectUri);

  // Refs
  const loadPromiseRef = useRef<Promise<void> | null>(null);

  // 设备检测（客户端）
  useEffect(() => {
    setIsMobile(isMobileDevice());
    setIsWeChat(isWeChatBrowser());
  }, []);

  // 处理错误
  const handleError = useCallback(
    (err: WechatLoginError) => {
      setError(err);
      onError?.(err);
    },
    [onError]
  );

  // 预加载 SDK
  const preloadSdk = useCallback(async (): Promise<void> => {
    // 如果已加载，直接返回
    if (window.WxLogin) {
      console.log("[useWechatLogin] SDK 已存在，跳过加载");
      setSdkStatus("loaded");
      return;
    }

    // 如果正在加载，返回现有 Promise
    if (loadPromiseRef.current) {
      console.log("[useWechatLogin] SDK 正在加载中，等待...");
      return loadPromiseRef.current;
    }

    console.log("[useWechatLogin] 开始加载 SDK");
    setSdkStatus("loading");
    setError(null);

    loadPromiseRef.current = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = WECHAT_SDK_URL;
      script.async = true;

      const timeoutId = setTimeout(() => {
        console.error("[useWechatLogin] SDK 加载超时");
        script.remove();
        loadPromiseRef.current = null;
        setSdkStatus("error");
        const err: WechatLoginError = {
          type: "TIMEOUT",
          message: "微信登录脚本加载超时",
        };
        handleError(err);
        reject(err);
      }, SDK_LOAD_TIMEOUT);

      script.onload = () => {
        clearTimeout(timeoutId);
        if (window.WxLogin) {
          console.log("[useWechatLogin] SDK 加载成功");
          setSdkStatus("loaded");
          resolve();
        } else {
          console.error("[useWechatLogin] SDK 加载后 WxLogin 不存在");
          loadPromiseRef.current = null;
          setSdkStatus("error");
          const err: WechatLoginError = {
            type: "SDK_LOAD_FAILED",
            message: "微信登录脚本加载失败",
          };
          handleError(err);
          reject(err);
        }
      };

      script.onerror = (e) => {
        console.error("[useWechatLogin] SDK 加载网络错误", e);
        clearTimeout(timeoutId);
        script.remove();
        loadPromiseRef.current = null;
        setSdkStatus("error");
        const err: WechatLoginError = {
          type: "NETWORK_ERROR",
          message: "网络连接失败，无法加载微信登录",
        };
        handleError(err);
        reject(err);
      };

      document.head.appendChild(script);
    });

    return loadPromiseRef.current;
  }, [handleError]);

  // 自动预加载
  useEffect(() => {
    if (autoPreload && isConfigValid && sdkStatus === "idle") {
      preloadSdk().catch(() => {
        // 预加载失败不阻塞，用户点击时会重试
      });
    }
  }, [autoPreload, isConfigValid, sdkStatus, preloadSdk]);

  // 刷新二维码
  const refreshQRCode = useCallback(() => {
    setState(generateState());
  }, []);

  // 处理登录点击
  const handleLogin = useCallback(() => {
    // 检查配置
    if (!isConfigValid) {
      const err: WechatLoginError = {
        type: "CONFIG_MISSING",
        message: "微信登录配置缺失",
      };
      handleError(err);
      return;
    }

    // 移动端或微信浏览器：直接跳转
    if (isMobile || isWeChat) {
      const authUrl = new URL(
        "https://open.weixin.qq.com/connect/oauth2/authorize"
      );
      authUrl.searchParams.set("appid", appId);
      authUrl.searchParams.set("redirect_uri", redirectUri);
      authUrl.searchParams.set("response_type", "code");
      authUrl.searchParams.set("scope", "snsapi_userinfo");
      // 将重定向路径编码到 state 中
      const stateWithRedirect = `${state}|${encodeURIComponent(redirectTo)}`;
      authUrl.searchParams.set("state", stateWithRedirect);
      authUrl.hash = "wechat_redirect";
      window.location.href = authUrl.toString();
      return;
    }

    // 桌面端：先生成新 state，再打开弹窗
    setState(generateState());
    setDialogOpen(true);
  }, [
    isConfigValid,
    isMobile,
    isWeChat,
    appId,
    redirectUri,
    state,
    handleError,
  ]);

  // 配置对象
  const config: WechatLoginConfig = {
    appId,
    redirectUri,
    scope: "snsapi_login",
    state: `${state}|${encodeURIComponent(redirectTo)}`,
  };

  return {
    sdkStatus,
    isMobile,
    isWeChat,
    isConfigValid,
    dialogOpen,
    setDialogOpen,
    handleLogin,
    preloadSdk,
    refreshQRCode,
    config,
    error,
  };
}
